/**
 * G01: Geodata Types
 * Type definitions for Polish geoportal integration
 */

// ============ Coordinate Types ============

/** WGS84 coordinates (GPS, EPSG:4326) */
export interface WGS84Coordinate {
  lat: number  // latitude, e.g., 52.2297
  lon: number  // longitude, e.g., 21.0122
}

/** PL-2000 coordinates (Polish national, EPSG:2180) */
export interface PL2000Coordinate {
  x: number  // easting, e.g., 638027.42
  y: number  // northing, e.g., 485064.76
}

/** Editor coordinates (50 dots per meter) */
export interface EditorCoordinate {
  x: number  // dots, positive = right
  y: number  // dots, positive = down (2D editor convention)
}

// ============ Geo-Anchor ============

/** Links real-world location to editor coordinate system */
export interface GeoAnchor {
  /** Real-world anchor point (WGS84) */
  origin: WGS84Coordinate
  /** Corresponding editor point (defaults to 0,0) */
  editorOrigin: EditorCoordinate
  /** Scale: dots per meter (default: 50) */
  dotsPerMeter: number
}

// ============ Geocoding (UUG) ============

export interface GeocodingResult {
  /** Matched address string */
  address: string
  /** Location in PL-2000 (as returned by API) */
  pl2000: PL2000Coordinate
  /** Location in WGS84 (converted) */
  wgs84: WGS84Coordinate
  /** Match confidence 0-1 */
  confidence: number
  /** Raw API response for debugging */
  raw?: unknown
}

export interface GeocodingError {
  type: 'not_found' | 'ambiguous' | 'network' | 'parse'
  message: string
  suggestions?: string[]  // For ambiguous matches
}

// ============ Parcel (ULDK) ============

export interface ParcelResult {
  /** Unique parcel identifier, e.g., "146510_8.0310.60" */
  id: string
  /** Boundary polygon vertices in WGS84 */
  boundary: WGS84Coordinate[]
  /** Boundary in PL-2000 (original from API) */
  boundaryPl2000: PL2000Coordinate[]
  /** Administrative region info */
  region: ParcelRegion
  /** Calculated area in m² */
  area: number
  /** Address if available (from geocoding) */
  address?: string
}

export interface ParcelRegion {
  voivodeship: string   // województwo
  county: string        // powiat
  commune: string       // gmina
  parcelNumber: string  // numer działki
}

export interface ParcelError {
  type: 'not_found' | 'network' | 'parse' | 'invalid_coordinates'
  message: string
}

// ============ Result Types ============

export type Result<T, E> =
  | { ok: true; value: T }
  | { ok: false; error: E }

// ============ Helper Functions ============

export function ok<T, E>(value: T): Result<T, E> {
  return { ok: true, value }
}

export function err<T, E>(error: E): Result<T, E> {
  return { ok: false, error }
}
